// Engineering Validation Engine v1.0
// Rule-based validation of client-requested changes against technical and safety constraints
// Behaves as an engineering reviewer — NOT a form validator

import { RFQState } from './rfqConfig';
import { EquipmentItem, RuleEngineOutput } from './ruleEngine';
import { ClientAdjustments } from './ReviewAdjustStep';

// ─── TYPES ───────────────────────────────────────────────────────────────────

export type ValidationStatus = 'accepted' | 'conditional' | 'rejected';

export interface ValidationItem {
  id: string;
  status: ValidationStatus;
  rule: string;
  equipment: string;
  message: string;
  technicalBasis: string;
  riskLevel?: 'low' | 'medium' | 'high' | 'critical';
}

export interface ValidationReport {
  overallStatus: 'Approved' | 'Conditional' | 'Rejected';
  timestamp: string;
  acceptedChanges: ValidationItem[];
  conditionalChanges: ValidationItem[];
  rejectedChanges: ValidationItem[];
  engineeringRecommendation: string;
  canSubmit: boolean;
  hasWarnings: boolean;
}

// ─── EQUIPMENT CLASSIFICATION ────────────────────────────────────────────────

const SAFETY_SYSTEM_KEYWORDS = [
  'Safety System', 'HSE Controls', 'SCBA', 'Gas Detector', 'Wind Sock',
  'Special Materials', 'NACE', 'Safety', 'HSE',
];

const SAND_EQUIPMENT_KEYWORDS = [
  'Desander', 'Sand Filter', 'Debris Catcher', 'Sand Management',
];

const LOGISTICS_POWER_KEYWORDS = [
  'Generator', 'Logistics Fleet', 'Site Support', 'Transport', 'Camp',
  'Power Supply', 'Welfare',
];

const PRESSURE_RATED_KEYWORDS = [
  'Choke Manifold', 'HP Lines', 'HP Iron', 'HP Pump', 'HP Nitrogen',
  'Test Manifold', 'Flowlines',
];

// ─── HELPER FUNCTIONS ────────────────────────────────────────────────────────

function isSafetyEquipment(name: string): boolean {
  return SAFETY_SYSTEM_KEYWORDS.some(kw => name.includes(kw));
}

function isSandEquipment(name: string): boolean {
  return SAND_EQUIPMENT_KEYWORDS.some(kw => name.includes(kw));
}

function isLogisticsPowerEquipment(name: string): boolean {
  return LOGISTICS_POWER_KEYWORDS.some(kw => name.includes(kw));
}

function isPressureRatedEquipment(name: string): boolean {
  return PRESSURE_RATED_KEYWORDS.some(kw => name.includes(kw));
}

function getEquipmentPressureRating(specification: string): number {
  // Extract pressure rating from specification string
  const match10k = specification.match(/10K/i);
  const match15k = specification.match(/15K/i);
  const match5k = specification.match(/5K/i);
  if (match15k) return 15000;
  if (match10k) return 10000;
  if (match5k) return 5000;
  return 0;
}

function getEquipmentCapacity(name: string, specification: string): { oilCapacity: number; gasCapacity: number } {
  // Extract capacity from separator specifications
  if (name === 'Test Separator') {
    if (specification.includes('Maximum') || specification.includes('>20K')) return { oilCapacity: 30000, gasCapacity: 0 };
    if (specification.includes('High') || specification.includes('5K-20K')) return { oilCapacity: 20000, gasCapacity: 0 };
    return { oilCapacity: 5000, gasCapacity: 0 };
  }
  if (name === 'Flare System') {
    if (specification.includes('Engineering Design') || specification.includes('>50')) return { oilCapacity: 0, gasCapacity: 100 };
    if (specification.includes('Upgraded') || specification.includes('>30')) return { oilCapacity: 0, gasCapacity: 50 };
    return { oilCapacity: 0, gasCapacity: 30 };
  }
  return { oilCapacity: 0, gasCapacity: 0 };
}

// ─── VALIDATION ENGINE ───────────────────────────────────────────────────────

export function runEngineeringValidation(
  rfqState: RFQState,
  ruleOutput: RuleEngineOutput,
  clientAdjustments: ClientAdjustments
): ValidationReport {
  const validationItems: ValidationItem[] = [];
  const pressure = parseFloat(rfqState.wellheadPressure) || 0;
  const oilRate = parseFloat(rfqState.oilRate) || 0;
  const gasRate = parseFloat(rfqState.gasRate) || 0;

  // Get removed equipment names
  const removedEquipment = clientAdjustments.equipment
    .filter(e => e.status === 'request-removal')
    .map(e => ({ name: e.name, specification: e.specification, reason: e.removalReason }));

  // Get added equipment
  const addedEquipment = clientAdjustments.additions;

  // ─── RULE 1: PRESSURE VALIDATION ────────────────────────────────────────
  if (pressure > 5000) {
    removedEquipment.forEach(removed => {
      if (isPressureRatedEquipment(removed.name)) {
        const rating = getEquipmentPressureRating(removed.specification);
        if (rating >= 10000) {
          // Client is trying to remove 10K-rated equipment when pressure demands it
          validationItems.push({
            id: `pressure-${removed.name.replace(/\s+/g, '-').toLowerCase()}`,
            status: 'rejected',
            rule: 'Pressure Validation (API 6A)',
            equipment: removed.name,
            message: 'Equipment not rated for required pressure — removal will create pressure containment failure risk',
            technicalBasis: `Wellhead pressure ${pressure} PSI exceeds 5,000 PSI threshold. ${removed.name} rated at ${rating} PSI is required per API 6A pressure class designation. Removal creates risk of catastrophic pressure containment failure.`,
            riskLevel: 'critical',
          });
        }
      }
    });

    // Check if any addition requests specify under-rated equipment
    addedEquipment.forEach(addition => {
      const descLower = addition.description.toLowerCase();
      if (descLower.includes('5k') || descLower.includes('5000 psi') || descLower.includes('5,000')) {
        if (descLower.includes('choke') || descLower.includes('manifold') || descLower.includes('line') || descLower.includes('iron')) {
          validationItems.push({
            id: `pressure-add-${addition.id}`,
            status: 'rejected',
            rule: 'Pressure Validation (API 6A)',
            equipment: addition.description,
            message: 'Requested equipment not rated for required pressure',
            technicalBasis: `Wellhead pressure ${pressure} PSI requires minimum 10K PSI-rated equipment. Requested item appears rated at 5,000 PSI — insufficient for operational conditions.`,
            riskLevel: 'critical',
          });
        }
      }
    });
  }

  // ─── RULE 2: H2S SAFETY VALIDATION ──────────────────────────────────────
  if (rfqState.h2sPresence === 'yes') {
    removedEquipment.forEach(removed => {
      if (isSafetyEquipment(removed.name)) {
        validationItems.push({
          id: `h2s-${removed.name.replace(/\s+/g, '-').toLowerCase()}`,
          status: 'rejected',
          rule: 'H₂S Safety Compliance (NACE MR0175)',
          equipment: removed.name,
          message: 'H₂S safety systems are mandatory — removal not permitted under any operational scenario',
          technicalBasis: `Confirmed H₂S presence requires full safety system deployment per NACE MR0175 and API RP 49. ${removed.name} is classified as life-safety equipment. Removal would violate regulatory requirements and expose personnel to lethal concentrations.`,
          riskLevel: 'critical',
        });
      }
    });
  }

  // ─── RULE 3: SAND MANAGEMENT VALIDATION ─────────────────────────────────
  if (rfqState.sandProduction === 'high') {
    removedEquipment.forEach(removed => {
      if (isSandEquipment(removed.name)) {
        validationItems.push({
          id: `sand-${removed.name.replace(/\s+/g, '-').toLowerCase()}`,
          status: 'conditional',
          rule: 'Sand Management (Erosion Risk)',
          equipment: removed.name,
          message: 'Risk of erosion and equipment damage — removal increases operational risk significantly',
          technicalBasis: `High sand production confirmed. ${removed.name} provides erosion protection for downstream equipment. Removal may result in accelerated wear on separators, chokes, and flowlines. Estimated equipment life reduction: 40-60%. Client assumes erosion damage liability.`,
          riskLevel: 'high',
        });
      }
    });
  } else if (rfqState.sandProduction === 'medium') {
    removedEquipment.forEach(removed => {
      if (isSandEquipment(removed.name)) {
        validationItems.push({
          id: `sand-med-${removed.name.replace(/\s+/g, '-').toLowerCase()}`,
          status: 'conditional',
          rule: 'Sand Management (Moderate Risk)',
          equipment: removed.name,
          message: 'Moderate erosion risk — removal acceptable with monitoring protocol',
          technicalBasis: `Medium sand production indicated. ${removed.name} removal increases erosion potential on downstream components. Acceptable with mandatory erosion monitoring program and reduced inspection intervals (every 72 hours vs. standard 168 hours).`,
          riskLevel: 'medium',
        });
      }
    });
  }

  // ─── RULE 4: LOGISTICS VALIDATION ───────────────────────────────────────
  if (rfqState.locationType === 'remote' || rfqState.locationType === 'desert') {
    removedEquipment.forEach(removed => {
      if (isLogisticsPowerEquipment(removed.name)) {
        validationItems.push({
          id: `logistics-${removed.name.replace(/\s+/g, '-').toLowerCase()}`,
          status: 'conditional',
          rule: 'Remote Operations Logistics',
          equipment: removed.name,
          message: 'Remote operations require self-contained support — removal creates operational dependency risk',
          technicalBasis: `Location classified as ${rfqState.locationType}. ${removed.name} provides autonomous operational capability. Removal requires client to guarantee equivalent infrastructure availability at site. Failure to provide creates risk of operational shutdown and equipment standby charges.`,
          riskLevel: 'medium',
        });
      }
    });
  }

  // ─── RULE 5: CAPACITY VALIDATION ────────────────────────────────────────
  // Check if client requests removal of capacity-critical equipment
  if (oilRate > 0) {
    removedEquipment.forEach(removed => {
      const capacity = getEquipmentCapacity(removed.name, removed.specification);
      if (capacity.oilCapacity > 0 && oilRate > capacity.oilCapacity * 0.5) {
        // Removing equipment that handles significant portion of oil rate
        if (removed.name === 'Test Separator') {
          validationItems.push({
            id: `capacity-oil-${removed.name.replace(/\s+/g, '-').toLowerCase()}`,
            status: 'rejected',
            rule: 'Capacity Validation (Process Sizing)',
            equipment: removed.name,
            message: 'Equipment capacity insufficient — no alternative separator in configuration to handle stated production rate',
            technicalBasis: `Stated oil production rate: ${oilRate} BOPD. ${removed.name} (${removed.specification}) is the only separator in configuration. Removal eliminates primary separation capacity. No alternative equipment available to process stated production volumes.`,
            riskLevel: 'critical',
          });
        }
      }
    });
  }

  if (gasRate > 0) {
    removedEquipment.forEach(removed => {
      const capacity = getEquipmentCapacity(removed.name, removed.specification);
      if (removed.name === 'Flare System') {
        validationItems.push({
          id: `capacity-gas-${removed.name.replace(/\s+/g, '-').toLowerCase()}`,
          status: 'rejected',
          rule: 'Capacity Validation (Gas Handling)',
          equipment: removed.name,
          message: 'Equipment capacity insufficient — flare system required for gas disposal per API RP 521',
          technicalBasis: `Stated gas rate: ${gasRate} MMSCFD. Flare system is mandatory for safe gas disposal during well testing operations. Removal violates API RP 521 requirements for pressure relief and depressurization systems. No alternative gas handling available.`,
          riskLevel: 'critical',
        });
      }
    });
  }

  // ─── VALIDATE ACCEPTED CHANGES ──────────────────────────────────────────
  // Equipment removals that pass all rules are classified as "Accepted"
  removedEquipment.forEach(removed => {
    const alreadyFlagged = validationItems.some(v => v.equipment === removed.name);
    if (!alreadyFlagged) {
      validationItems.push({
        id: `accepted-${removed.name.replace(/\s+/g, '-').toLowerCase()}`,
        status: 'accepted',
        rule: 'Configuration Review',
        equipment: removed.name,
        message: 'Removal accepted — no engineering or safety constraints identified',
        technicalBasis: `${removed.name} removal does not conflict with stated operational parameters. Client justification: "${removed.reason}". Change accepted subject to final engineering review during detailed design phase.`,
        riskLevel: 'low',
      });
    }
  });

  // Validate addition requests (accepted by default unless they conflict)
  addedEquipment.forEach(addition => {
    const alreadyFlagged = validationItems.some(v => v.id === `pressure-add-${addition.id}`);
    if (!alreadyFlagged) {
      validationItems.push({
        id: `accepted-add-${addition.id}`,
        status: 'accepted',
        rule: 'Addition Request',
        equipment: addition.description,
        message: 'Addition request acknowledged — subject to availability and engineering compatibility review',
        technicalBasis: `Client requests addition of: "${addition.description}". Justification: "${addition.justification}". To be evaluated during detailed engineering phase for compatibility with existing package configuration.`,
        riskLevel: 'low',
      });
    }
  });

  // ─── CLASSIFY RESULTS ──────────────────────────────────────────────────
  const accepted = validationItems.filter(v => v.status === 'accepted');
  const conditional = validationItems.filter(v => v.status === 'conditional');
  const rejected = validationItems.filter(v => v.status === 'rejected');

  // Determine overall status
  let overallStatus: 'Approved' | 'Conditional' | 'Rejected';
  if (rejected.length > 0) {
    overallStatus = 'Rejected';
  } else if (conditional.length > 0) {
    overallStatus = 'Conditional';
  } else {
    overallStatus = 'Approved';
  }

  // Generate engineering recommendation
  const recommendation = generateRecommendation(overallStatus, rejected, conditional, accepted, rfqState);

  return {
    overallStatus,
    timestamp: new Date().toISOString(),
    acceptedChanges: accepted,
    conditionalChanges: conditional,
    rejectedChanges: rejected,
    engineeringRecommendation: recommendation,
    canSubmit: rejected.length === 0,
    hasWarnings: conditional.length > 0,
  };
}

// ─── RECOMMENDATION GENERATOR ────────────────────────────────────────────────

function generateRecommendation(
  status: 'Approved' | 'Conditional' | 'Rejected',
  rejected: ValidationItem[],
  conditional: ValidationItem[],
  accepted: ValidationItem[],
  rfqState: RFQState
): string {
  if (status === 'Rejected') {
    const criticalCount = rejected.filter(r => r.riskLevel === 'critical').length;
    let rec = `SUBMISSION BLOCKED — ${rejected.length} change(s) violate engineering or safety constraints and cannot proceed.\n\n`;
    rec += `Action Required:\n`;
    rejected.forEach((r, i) => {
      rec += `${i + 1}. Reinstate ${r.equipment} — ${r.message}\n`;
    });
    rec += `\nReturn to Review & Adjust step to reinstate rejected items before resubmission.`;
    if (criticalCount > 0) {
      rec += ` ${criticalCount} item(s) classified as CRITICAL safety risk.`;
    }
    return rec;
  }

  if (status === 'Conditional') {
    let rec = `SUBMISSION PERMITTED WITH CONDITIONS — ${conditional.length} change(s) carry elevated operational risk.\n\n`;
    rec += `Conditions for Acceptance:\n`;
    conditional.forEach((c, i) => {
      rec += `${i + 1}. ${c.equipment}: ${c.technicalBasis.split('.').slice(-2, -1)[0]?.trim() || c.message}\n`;
    });
    rec += `\nClient acknowledges acceptance of identified risks. EnergiPro engineering team will include risk mitigation measures in final proposal where applicable.`;
    if (accepted.length > 0) {
      rec += ` ${accepted.length} other change(s) accepted without conditions.`;
    }
    return rec;
  }

  // Approved
  if (accepted.length === 0) {
    return `NO CHANGES REQUESTED — Original engineering configuration accepted as proposed. Submission may proceed.`;
  }
  return `ALL CHANGES ACCEPTED — ${accepted.length} requested modification(s) validated against engineering constraints. No safety or capacity conflicts identified. Submission may proceed. Final configuration subject to detailed engineering review during proposal development.`;
}

// ─── EXPORT UTILITIES ────────────────────────────────────────────────────────

export function getValidationStatusColor(status: ValidationStatus): string {
  switch (status) {
    case 'accepted': return 'green';
    case 'conditional': return 'yellow';
    case 'rejected': return 'red';
  }
}

export function formatValidationReport(report: ValidationReport): string {
  let text = `ENGINEERING VALIDATION REPORT\n`;
  text += `Generated: ${new Date(report.timestamp).toLocaleString()}\n`;
  text += `Overall Status: ${report.overallStatus}\n\n`;

  if (report.acceptedChanges.length > 0) {
    text += `ACCEPTED CHANGES (${report.acceptedChanges.length}):\n`;
    report.acceptedChanges.forEach(c => {
      text += `• ${c.equipment} — ${c.message}\n`;
    });
    text += '\n';
  }

  if (report.conditionalChanges.length > 0) {
    text += `CONDITIONAL CHANGES (${report.conditionalChanges.length}):\n`;
    report.conditionalChanges.forEach(c => {
      text += `• ${c.equipment} — ${c.message}\n  Risk: ${c.technicalBasis}\n`;
    });
    text += '\n';
  }

  if (report.rejectedChanges.length > 0) {
    text += `REJECTED CHANGES (${report.rejectedChanges.length}):\n`;
    report.rejectedChanges.forEach(c => {
      text += `• ${c.equipment} — ${c.message}\n  Basis: ${c.technicalBasis}\n`;
    });
    text += '\n';
  }

  text += `ENGINEERING RECOMMENDATION:\n${report.engineeringRecommendation}\n`;
  return text;
}
